/**
 * The company last selected in each building, persisted in localStorage.
 * Kept apart from the store so the rules can be tested without a browser.
 * `storage` is injectable for the same reason.
 */

type CompanyStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

const LEGACY_KEY = "selectedCompany";
const keyFor = (buildingId: string) => `${LEGACY_KEY}:${buildingId}`;

// localStorage throws when storage is disabled or full (e.g. Safari private
// mode). A lost selection only means falling back to the user's own company.
const read = (storage: CompanyStorage, key: string) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};
const write = (storage: CompanyStorage, key: string, value: string | null) => {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // See read.
  }
};

/** The single pre-per-building selection. Consumed by the first building
 * that has nothing stored, then deleted: honouring it in every building would
 * carry one building's company into the others. */
const takeLegacyCompany = (storage: CompanyStorage) => {
  const legacy = read(storage, LEGACY_KEY);
  if (legacy === null) return "";
  write(storage, LEGACY_KEY, null);
  try {
    return JSON.parse(legacy).value || "";
  } catch {
    return legacy;
  }
};

/** The company last selected in a building. Remembered per building since a
 * company id from one building means nothing in another. */
export const getStoredCompany = (
  buildingId: string,
  storage: CompanyStorage = localStorage,
) => {
  if (!buildingId) return "";
  const perBuilding = read(storage, keyFor(buildingId));
  if (perBuilding !== null) return perBuilding;
  const legacy = takeLegacyCompany(storage);
  if (legacy) write(storage, keyFor(buildingId), legacy);
  return legacy;
};

export const storeCompany = (
  buildingId: string,
  companyId: string,
  storage: CompanyStorage = localStorage,
) => {
  if (buildingId) write(storage, keyFor(buildingId), companyId);
};

/** Forgets one building's selection (and the legacy one), keeping the rest:
 * a failure in one building says nothing about the others. */
export const clearStoredCompany = (
  buildingId: string,
  storage: CompanyStorage = localStorage,
) => {
  write(storage, LEGACY_KEY, null);
  if (buildingId) write(storage, keyFor(buildingId), null);
};

/** Removes every remembered company selection, per-building and legacy. */
export const clearStoredCompanies = (storage: CompanyStorage = localStorage) => {
  try {
    const keys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i),
    );
    keys
      .filter(
        (key): key is string =>
          key === LEGACY_KEY || !!key?.startsWith(`${LEGACY_KEY}:`),
      )
      .forEach((key) => storage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was stored either.
  }
};

/**
 * Which company to select on entering a building, given what it remembers.
 * Same rules useSelectedCompany enforces once the building is shown: only the
 * user's own company or one they administer can stay selected.
 */
export const resolveBuildingSelection = (
  user: { buildingTenantId: string; adminInCompanies?: string[] },
  stored: string,
) => {
  const adminInStored = !!stored && !!user.adminInCompanies?.includes(stored);
  const selectedCompany =
    adminInStored || stored === user.buildingTenantId
      ? stored
      : user.buildingTenantId;
  return {
    selectedCompany,
    adminInSelectedCompany: !!user.adminInCompanies?.includes(selectedCompany),
  };
};
